"use client";

import {
  SetReflectionRequest,
  ThoughtDimensionAttributes,
} from "@/generated/fastapi";
import { useSetReflectionMutation } from "@/features/thoughts";
import {
  getDefaultAssessmentInstrumentId,
  getLikert5pAgreementOptions,
} from "@/shared/config/backend";
import { Box, HStack, RadioGroup, Text } from "@chakra-ui/react";
import { useCallback } from "react";
import { useState } from "react";
import {
  type QuestionnaireItem,
  type QuestionnaireThoughtOriented,
} from "../types";
import { useAnswers } from "../hooks/useAnswers";
import {
  QUESTIONNAIRE_CHECKMARK_COLOR,
  QUESTIONNAIRE_CHECKMARK_POSITION,
  QUESTIONNAIRE_CHECKMARK_SIZE,
  QUESTIONNAIRE_SHOW_CHECKMARK,
  QUESTIONNAIRE_SUCCESS_BG_COLOR,
} from "../config";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import { normalizeIconSize } from "@/shared/ui/actions/GenericIcon";
import type { ResponsiveIconSize } from "@/shared/ui/actions/GenericIcon";
import { showErrorToast } from "@/shared/feedback";
import ReflectionFeedbackAction from "./ReflectionFeedbackAction";

interface QuestionnaireThoughItemProps {
  question: QuestionnaireItem;
  thought: QuestionnaireThoughtOriented;
  iconSize?: ResponsiveIconSize;
  onAnswerProvided?: (thoughtId: string, dimension: string) => void; // Add callback prop
}

export default function QuestionnaireThoughItem({
  question,
  thought,
  iconSize = "md",
  onAnswerProvided,
}: QuestionnaireThoughItemProps) {
  const { isAnswered, shouldShow, animationStyles, handleAnswer } =
    useAnswers();
  const setReflectionMutation = useSetReflectionMutation();

  // Get the dimension data for this specific dimension
  const dimensionData = thought.dimensions[question.dimension];
  const normalizedIconSize = normalizeIconSize(iconSize);

  // Local state to manage the new score
  const [newScore, setNewScore] = useState<string | null>(
    dimensionData?.score?.toString() || null,
  );

  // Use useCallback for event handlers to prevent unnecessary re-renders
  const handleScoreChange = useCallback(
    (value: string) => {
      if (!value) return;

      setNewScore(value); // Local state update

      const request: SetReflectionRequest = {
        thought_id: thought.id,
        dimension_index: question.dimension as ThoughtDimensionAttributes,
        score: parseInt(value, 10),
        instrument: getDefaultAssessmentInstrumentId(),
      };

      // Fire and forget backend update!
      setReflectionMutation
        .mutateAsync(request)
        .then(() => {
          handleAnswer(() => {
            setNewScore(null);
            onAnswerProvided?.(thought.id, question.dimension);
          });
        })
        .catch(() => {
          showErrorToast("Failed to save reflection.");
        });
    },
    [
      thought.id,
      question.dimension,
      onAnswerProvided,
      handleAnswer,
      setReflectionMutation,
    ],
  );

  if (!shouldShow) return null;

  return (
    <Box
      borderRadius="md"
      position="relative"
      bg={isAnswered ? QUESTIONNAIRE_SUCCESS_BG_COLOR : "transparent"}
      style={animationStyles}
    >
      {/* Checkmark icon overlay */}
      {isAnswered && QUESTIONNAIRE_SHOW_CHECKMARK && (
        <Box
          position="absolute"
          top={QUESTIONNAIRE_CHECKMARK_POSITION === "top-right" ? 2 : "50%"}
          right={QUESTIONNAIRE_CHECKMARK_POSITION === "top-right" ? 2 : "50%"}
          transform={
            QUESTIONNAIRE_CHECKMARK_POSITION === "center"
              ? "translate(50%, -50%)"
              : undefined
          }
          zIndex={10}
        >
          <GenericIcon
            icon="confirm"
            size={QUESTIONNAIRE_CHECKMARK_SIZE}
            color={QUESTIONNAIRE_CHECKMARK_COLOR}
          />
        </Box>
      )}

      <HStack align="start" justify="space-between" gap="2">
        <Text fontSize="sm" flex="1" minW="0">
          {question.question}
        </Text>
        <ReflectionFeedbackAction
          thoughtId={thought.id}
          thoughtWording={thought.mindstring}
          dimensionKey={question.dimension}
          questionSnapshot={question.question}
        />
      </HStack>

      <RadioGroup.Root
        value={newScore}
        onValueChange={(e) => handleScoreChange(e.value || "")}
        disabled={isAnswered}
      >
        <HStack>
          {getLikert5pAgreementOptions().map((item) => (
            <Box key={item.value} padding="1" flex="1" position="relative">
              <RadioGroup.Item value={item.value} width="full" height="full">
                <RadioGroup.ItemHiddenInput />
                <Box
                  // @ts-ignore This works with Chakra UI
                  color={item.color}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <GenericIcon
                    icon={item.icon}
                    humanReadable={item.label}
                    size={normalizedIconSize}
                  />
                </Box>
              </RadioGroup.Item>
            </Box>
          ))}
        </HStack>
      </RadioGroup.Root>
    </Box>
  );
}
